import { useLocation } from "@reach/router"
import * as React from "react"

type Options = {
  buttonIds: string[]
  match?: "all" | "any"
  scriptId?: string // Only used if we are the owner loading the script (not GTM)
  scriptUrl?: string // Optional; omit if GTM owns Termly
  timeoutMs?: number
}

declare global {
  interface Window {
    // Termly embed (GTM) exposes this:
    displayPreferenceModal?: () => void
    // Some builds also expose an event hub here:
    Termly?: {
      off?: (evt: string, fn: () => void) => void
      on?: (evt: string, fn: () => void) => void
    }

    // Resource-blocker build sometimes exposes this namespace:
    termly?: {
      openConsentManager?: () => void
      openConsentPreferences?: () => void
    }

    TERMLY_RESOURCE_BLOCKER_LOADED?: boolean
  }
}

export function useTermlyConsent({
  buttonIds,
  match = "all",
  scriptId = "termlyScript",
  scriptUrl, // omit if GTM owns Termly
  timeoutMs = 10000,
}: Options) {
  const location = useLocation()
  const wiredRef = React.useRef(false) // Prevent StrictMode double-wiring

  React.useEffect(() => {
    if (typeof document === "undefined") {
      return
    }

    if (wiredRef.current) {
      return
    }

    const { disconnect, done } = waitForElements(buttonIds, match, timeoutMs)
    let cancelled = false

    done.then(() => {
      if (cancelled) {
        return
      }

      // Detect if Termly (of either flavor) is already present.
      const termlyAlreadyPresent =
        !!document.getElementById(scriptId) ||
        !!document.getElementById("termly-code-snippet-support") ||
        typeof window.displayPreferenceModal === "function" || // GTM embed path ready
        typeof window.Termly !== "undefined" || // GTM embed path scaffold
        window.TERMLY_RESOURCE_BLOCKER_LOADED === true || // resource-blocker path
        typeof window.termly !== "undefined" // resource-blocker API

      // If not present and a scriptUrl was provided, we are the owner → inject exactly once.
      if (!termlyAlreadyPresent && scriptUrl) {
        injectScriptOnce(scriptUrl, scriptId).catch((e) => {
          console.warn("Termly script failed to load:", e)
        })
      }

      // Wait until an opener exists (handles GTM embed init timing).
      waitForPreferenceOpener(timeoutMs).then(() => {
        if (cancelled) {
          return
        }
        const removeHandler = delegateButtons(buttonIds, () =>
          openPreferencesSafe()
        )
        wiredRef.current = true

        // Teardown: remove click delegation ONLY. Never remove the Termly script.
        const teardown = () => {
          removeHandler()
        }

        // If the effect cleans up before we got here, run teardown immediately.
        if (cancelled) {
          teardown()
          return
        }

        // Attach to this effect's cleanup via a closure:
        ;((cleanupFns) => cleanupFns.push(teardown))(effectCleanups)
      })
    })

    // Track cleanup fns created after async waits
    const effectCleanups: Array<() => void> = []

    return () => {
      cancelled = true
      disconnect()
      // Run any deferred cleanups
      for (const fn of effectCleanups) {
        try {
          fn()
        } catch {
          // no-op
        }
      }
    }
    // Join to prevent re-runs due to array identity changes
  }, [buttonIds.join("|"), match, timeoutMs, scriptId, scriptUrl])

  // Intentionally empty: Termly is a singleton per page load.
  React.useEffect(() => {
    // Do not remove/reinit Termly on route changes.
  }, [location.pathname])
}

function delegateButtons(buttonIds: string[], onClick: () => void) {
  // Event delegation means we’re resilient to re-renders and display:none swaps.
  const idSet = new Set(buttonIds)

  const handler = (e: Event) => {
    const target = e.target as HTMLElement | null
    if (!target) {
      return
    }

    // Walk up to catch clicks on inner spans, icons, etc.
    let el: HTMLElement | null = target
    while (el && el !== document.body) {
      if (el.id && idSet.has(el.id)) {
        e.preventDefault()
        onClick()
        return
      }
      el = el.parentElement
    }
  }

  // Use capture to get in before other handlers that might stopPropagation
  document.addEventListener("click", handler, true)
  return () => {
    document.removeEventListener("click", handler, true)
  }
}

function injectScriptOnce(url: string, scriptId: string) {
  return new Promise<void>((resolve, reject) => {
    if (typeof document === "undefined") {
      resolve()
      return
    }

    const existing = document.getElementById(
      scriptId
    ) as HTMLScriptElement | null
    if (existing) {
      if ((existing as any)._loaded) {
        resolve()
        return
      }
      existing.addEventListener("load", () => resolve())
      existing.addEventListener("error", (e) => reject(e))
      return
    }

    const script = document.createElement("script")
    script.id = scriptId
    script.src = url
    script.type = "text/javascript"
    script.async = true
    ;(script as any)._loaded = false

    script.addEventListener("load", () => {
      ;(script as any)._loaded = true
      resolve()
    })
    script.addEventListener("error", (e) => reject(e))

    document.body.appendChild(script)
  })
}

/**
 * Preferred opener order:
 *  1) GTM embed build: window.displayPreferenceModal()
 *  2) Resource-blocker API (if you later let the hook own loading):
 *     window.termly.openConsentPreferences() / openConsentManager()
 *  3) DOM fallback: click a visible preferences control if present.
 */
function openPreferencesSafe() {
  if (typeof window.displayPreferenceModal === "function") {
    window.displayPreferenceModal()
    return
  }
  if (typeof window.termly?.openConsentPreferences === "function") {
    window.termly.openConsentPreferences()
    return
  }
  if (typeof window.termly?.openConsentManager === "function") {
    window.termly.openConsentManager()
    return
  }

  // Fallbacks if banner/links are on-screen:
  const el =
    (document.querySelector(".t-preference-button") as HTMLElement | null) ||
    (document.querySelector(
      '[data-testid="preferences-link"]'
    ) as HTMLElement | null)
  el?.click()
}

function waitForElements(
  buttonIds: string[],
  mode: "all" | "any",
  timeoutMs = 10000
) {
  let resolve!: (value: boolean) => void
  const done = new Promise<boolean>((res) => (resolve = res))

  const getEl = (id: string) =>
    typeof document !== "undefined" ? document.getElementById(id) : null

  const ready =
    mode === "all"
      ? () => buttonIds.every((id) => !!getEl(id))
      : () => buttonIds.some((id) => !!getEl(id))

  if (ready()) {
    resolve(true)
    return {
      disconnect: () => {
        // no-op
      },
      done,
    }
  }

  const observer = new MutationObserver(() => {
    if (ready()) {
      observer.disconnect()
      resolve(true)
    }
  })

  observer.observe(document.documentElement, { childList: true, subtree: true })

  const timeoutId =
    timeoutMs > 0
      ? window.setTimeout(() => {
          observer.disconnect()
          resolve(false)
        }, timeoutMs)
      : null

  const disconnect = () => {
    observer.disconnect()
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId)
    }
  }

  return { disconnect, done }
}

/**
 * Wait until an opener is available. We first try the event bus if present,
 * then poll briefly as a safety net. Resolves even if we time out—clicks will
 * still try DOM fallbacks in openPreferencesSafe().
 */
function waitForPreferenceOpener(timeoutMs = 10000) {
  return new Promise<void>((resolve) => {
    // Already ready?
    if (
      typeof window.displayPreferenceModal === "function" ||
      typeof window.termly?.openConsentPreferences === "function" ||
      typeof window.termly?.openConsentManager === "function"
    ) {
      resolve()
      return
    }

    let done = false
    const finish = () => {
      if (done) {
        return
      }
      if (
        typeof window.displayPreferenceModal === "function" ||
        typeof window.termly?.openConsentPreferences === "function" ||
        typeof window.termly?.openConsentManager === "function"
      ) {
        done = true
        cleanup()
        resolve()
      }
    }

    const handlers: Array<() => void> = []

    // Try to subscribe to plausible embed lifecycle events
    const tryOn = (evt: string) => {
      if (
        typeof window.Termly?.on === "function" &&
        typeof window.Termly?.off === "function"
      ) {
        const h = () => finish()
        window.Termly.on(evt, h)
        handlers.push(() => window.Termly?.off?.(evt, h))
      }
    }

    // Common event names used by Termly embed builds (defensive: add a few)
    tryOn("initialized")
    tryOn("consentManagerInitialized")
    tryOn("widgetLoad")
    tryOn("onWidgetLoadComplete")

    // Poll as a cross-build safety net
    const pollId = window.setInterval(finish, 200)
    const timeoutId = window.setTimeout(() => {
      done = true
      cleanup()
      resolve() // Still resolve—click handler will fall back to DOM click
    }, timeoutMs)

    const cleanup = () => {
      window.clearInterval(pollId)
      window.clearTimeout(timeoutId)
      for (const off of handlers) {
        try {
          off()
        } catch {
          // no-op
        }
      }
    }

    // Try once immediately
    finish()
  })
}
